import { createContext, useCallback, useMemo, useState, type PropsWithChildren } from 'react';

import {
  NewPracticeModal,
  type CustomTestSettings,
  type NewPracticeModalStep,
  type TargetedPracticeSettings,
} from '../ui/NewPracticeModal';

interface PracticeModalContextValue {
  openPracticeModal: (step?: NewPracticeModalStep) => void;
  closePracticeModal: () => void;
}

interface PracticeModalProviderProps extends PropsWithChildren {
  onStartCustomTest?: (settings: CustomTestSettings) => void;
  onStartTargetedPractice?: (settings: TargetedPracticeSettings) => void;
}

const PracticeModalContext = createContext<PracticeModalContextValue | null>(null);
export { PracticeModalContext };
export type { PracticeModalContextValue };

export const PracticeModalProvider = ({
  children,
  onStartCustomTest,
  onStartTargetedPractice,
}: PracticeModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialStep, setInitialStep] = useState<NewPracticeModalStep>('menu');

  const openPracticeModal = useCallback((step: NewPracticeModalStep = 'menu') => {
    setInitialStep(step);
    setIsOpen(true);
  }, []);

  const closePracticeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const contextValue = useMemo<PracticeModalContextValue>(
    () => ({
      openPracticeModal,
      closePracticeModal,
    }),
    [closePracticeModal, openPracticeModal]
  );

  return (
    <PracticeModalContext.Provider value={contextValue}>
      {children}
      <NewPracticeModal
        isOpen={isOpen}
        initialStep={initialStep}
        onClose={closePracticeModal}
        onStartCustomTest={onStartCustomTest}
        onStartTargetedPractice={onStartTargetedPractice}
      />
    </PracticeModalContext.Provider>
  );
};
